import * as React from "react";
import {useEffect, useState} from "react";
import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import Person from "../../../model/Person";
import LongTermCareStrategyChange, {
  ILongTermCarePlan,
  ILongTermCareStrategyChange,
  LTCStrategyType
} from "../../../model/changes/LongTermCareStrategyChange";
import {
  getISODateFromDate,
  getISODateToday,
  humanizeString,
  isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat,
} from "../../../stores/StoreUtilities";
import {useResources} from "../../../stores/ResourceProvider";
import {
  Box,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  Radio,
  RadioGroup,
  Typography,
  useTheme
} from "@mui/material";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreatePlanChangeInput, UpdatePlanChangeInput} from "../../../API";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Tracking from "../../../components/Tracking";
import {Frequency} from "../../../model/Schedule";
import Duration, {DurationUnits} from "../../../model/Duration"
import Asset, {AssetCategory} from "../../../model/Asset";
import {isNumber} from "../../../model/ModelUtilities";
import DurationPickerValidator from "../../../components/form/DurationPickerValidator";
import {addMonths, addYears, differenceInCalendarYears} from "date-fns";
import Milestone from "../../../model/Milestone";
import {globalColors} from "../../../styles/globalStyles";


const LongTermCareStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open? : boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [planModel, setPlanModel] = useState<Plan>(plan)
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [planChange, setPlanChange] = useState<LongTermCareStrategyChange | undefined>()

  const [owner_1, setOwner_1] = useState<Person | undefined>()
  const [strategyType_1, setStrategyType_1] = useState<LTCStrategyType>(LTCStrategyType.None)
  const [careAmount_1, setCareAmount_1] = useState<string | undefined>()
  const [careFrequency_1, setCareFrequency_1] = useState<Frequency>(Frequency.Monthly)
  const [careStart_1, setCareStart_1] = useState<string>()
  const [careDuration_1, setCareDuration_1] = useState<Duration | undefined>()
  const [careAnnualInf_1, setCareAnnualInf_1] = useState<string | undefined>()
  const [asset_1, setAsset_1] = useState<Asset | undefined>()
  const [premiumAmount_1, setPremiumAmount_1] = useState<string | undefined>()
  const [premiumAnnualInf_1, setPremiumAnnualInf_1] = useState<string | undefined>()
  const [premiumStart_1, setPremiumStart_1] = useState<string>()
  const [premiumFrequency_1, setPremiumFrequency_1] = useState<Frequency>(Frequency.Monthly)
  const [premiumWaiver_1, setPremiumWaiver_1] = useState<boolean>(false)
  const [benefitAmount_1, setBenefitAmount_1] = useState<string | undefined>()
  const [benefitFrequecy_1, setBenefitFrequency_1] = useState<Frequency>(Frequency.Monthly)
  const [benefitAnnualInf_1, setBenefitAnnualInf_1] = useState<string | undefined>()
  const [benefitDuration_1, setBenefitDuration_1] = useState<Duration | undefined>( new Duration({amount:0, units:DurationUnits.Years}))
  const [ageMilestones_1, setAgeMilestones_1] = useState<Milestone[]>([])
  
  const [owner_2, setOwner_2] = useState<Person | undefined>()
  const [strategyType_2, setStrategyType_2] = useState<LTCStrategyType>(LTCStrategyType.None)
  const [careAmount_2, setCareAmount_2] = useState<string | undefined>()
  const [careFrequency_2, setCareFrequency_2] = useState<Frequency>(Frequency.Monthly)
  const [careStart_2, setCareStart_2] = useState<string>()
  const [careDuration_2, setCareDuration_2] = useState<Duration | undefined>()
  const [careAnnualInf_2, setCareAnnualInf_2] = useState<string | undefined>()
  const [asset_2, setAsset_2] = useState<Asset | undefined>()
  const [premiumAmount_2, setPremiumAmount_2] = useState<string | undefined>()
  const [premiumAnnualInf_2, setPremiumAnnualInf_2] = useState<string | undefined>()
  const [premiumStart_2, setPremiumStart_2] = useState<string>()
  const [premiumFrequency_2, setPremiumFrequency_2] = useState<Frequency>(Frequency.Monthly)
  const [premiumWaiver_2, setPremiumWaiver_2] = useState<boolean>(false)
  const [benefitAmount_2, setBenefitAmount_2] = useState<string | undefined>()
  const [benefitAnnualInf_2, setBenefitAnnualInf_2] = useState<string | undefined>()
  const [benefitFrequecy_2, setBenefitFrequency_2] = useState<Frequency>(Frequency.Monthly)
  const [benefitDuration_2, setBenefitDuration_2] = useState<Duration | undefined>(new Duration({amount:0, units:DurationUnits.Years}))
  const [ageMilestones_2, setAgeMilestones_2] = useState<Milestone[]>([])

  const { modelStore, notify } = useResources()

  const theme = useTheme()
  const styles = {
    formColumn: {
      display: "flex",
      flexGrow: 1,
      justifyContent: "stretch"
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"]
    },
    calcLink: {
      fontWeight: 700,
      textDecoration: "underline"
    },
    section: {
      backgroundColor: globalColors.details,
      marginBottom: "4px",
      paddingLeft: "4px",
      paddingRight: "4px"
    }
  }

  useEffect(() => {
    setPlanModel(plan)
    setIsOpen(open === true)
    let change: LongTermCareStrategyChange = plan.getChange(PlanChangeType.LongTermCareStrategy) as LongTermCareStrategyChange
    if (!change) {
      change = new LongTermCareStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.LongTermCareStrategy,
        name: "Long Term Care Strategy",
        description: "",
        enabled: true,
        details: "[]"
      })
    }
    if (!change.plans) {
      change.plans = []
    }
    if (model.person1) {
      const person1 = model.person1
      let ltc = change.plans.find((p: ILongTermCarePlan) => p.ownerId === model.person1!.id)
      if (!ltc || ltc.strategyType === LTCStrategyType.None) {
        setOwner_1(person1)
        const start = getDefaultStartDate(person1)
        setCareStart_1(start)
        setCareDuration_1(getDefaultDuration(person1, start))
        setPremiumStart_1(getISODateToday())
      } else {
        setOwner_1(person1)
        setStrategyType_1(ltc.strategyType || LTCStrategyType.None)
        setCareStart_1(ltc.careStart)
        setCareDuration_1(ltc.careDuration)
        setCareAmount_1(numberToMoneyFormat(ltc.careAmount))
        setCareAnnualInf_1(isNumber(ltc.careAnnualInf) ? numberToPercentFormat(ltc.careAnnualInf!) : "")
        if (ltc.assetId) {
          setAsset_1(model.getAssetById(ltc.assetId))
        }
        setPremiumAmount_1(numberToMoneyFormat(ltc.premiumAmount))
        setPremiumAnnualInf_1(isNumber(ltc.premiumAnnualInf) ? numberToPercentFormat(ltc.premiumAnnualInf!) : "")
        setPremiumFrequency_1(ltc.premiumFrequency || Frequency.Monthly)
        setPremiumStart_1(ltc.premiumStart ?? getISODateToday())
        setPremiumWaiver_1(ltc.premiumWaiver === true)
        setBenefitAmount_1(numberToMoneyFormat(ltc.benefitAmount))
        setBenefitAnnualInf_1(isNumber(ltc.benefitAnnualInf) ? numberToPercentFormat(ltc.benefitAnnualInf!) : "")
        setBenefitDuration_1(ltc.benefitDuration)
      }
      setAgeMilestones_1(person1.getAgeMilestones())
    }

    if (model.person2) {
      const person2 = model.person2
      let ltc = change.plans.find((p: ILongTermCarePlan) => p.ownerId === person2!.id)
      if (!ltc || ltc.strategyType === LTCStrategyType.None) {
        setOwner_2(person2)
        const start = getDefaultStartDate(person2)
        setCareStart_2(start)
        setCareDuration_2(getDefaultDuration(person2, start))
        setPremiumStart_2(getISODateToday())
      } else {
        setOwner_2(person2)
        setStrategyType_2(ltc.strategyType || LTCStrategyType.None)
        setCareStart_2(ltc.careStart)
        setCareDuration_2(ltc.careDuration)
        setCareAmount_2(numberToMoneyFormat(ltc.careAmount))
        setCareAnnualInf_2(isNumber(ltc.careAnnualInf) ? numberToPercentFormat(ltc.careAnnualInf!) : "")
        if (ltc.assetId) {
          setAsset_2(model.getAssetById(ltc.assetId))
        }
        setPremiumAmount_2(numberToMoneyFormat(ltc.premiumAmount))
        setPremiumAnnualInf_2(isNumber(ltc.premiumAnnualInf) ? numberToPercentFormat(ltc.premiumAnnualInf!) : "")
        setPremiumFrequency_2(ltc.premiumFrequency || Frequency.Monthly)
        setPremiumStart_2(ltc.premiumStart ?? getISODateToday())
        setPremiumWaiver_2(ltc.premiumWaiver === true)
        setBenefitAmount_2(numberToMoneyFormat(ltc.benefitAmount))
        setBenefitAnnualInf_2(isNumber(ltc.benefitAnnualInf) ? numberToPercentFormat(ltc.benefitAnnualInf!) : "")
        setBenefitDuration_2(ltc.benefitDuration)
      }
      setAgeMilestones_2(person2.getAgeMilestones())
    }
    setPlanChange(change)

  }, [plan])

  const getDefaultStartDate = (person: Person) => {
    let startDate: string | undefined = undefined
    const age = person.getAge()
    const lifeSpan = person.getLifeExpectancyYears()
    const remainingYears = lifeSpan - age
    let startAge = 0
    if (remainingYears > 3) {
      startAge = lifeSpan - 3
    } else if (remainingYears > 1) {
      startAge = lifeSpan - 1
    }

    if (startAge > 0) {
      startDate = getISODateFromDate(person.getDateOnAge(startAge))
    } else {
      startDate = getISODateToday()
    }

    return startDate
  }

  const getDefaultDuration = (person: Person, start : string) => {
    const endDate = person.getDateOnAge(person.getLifeExpectancyYears())
    const years = differenceInCalendarYears(endDate, isoToLocalDate(start))
    return new Duration({
      amount: years,
      units: DurationUnits.Years
    })
    // return new Duration({
    //   amount: 1,
    //   units: DurationUnits.Lifetime
    // })
  }

  const getCareStartDate = (person: Person, duration: Duration) => {
    let startDate: Date | undefined = undefined
    const lifeSpan = person.getLifeExpectancyYears()
    const endDate = person.getDateOnAge(lifeSpan)
    if (duration.units === DurationUnits.Months) {
      startDate = addMonths(endDate, -duration.amount)
    } else if (duration.units === DurationUnits.Years) {
      startDate = addYears(endDate, -duration.amount)
    }
    if (startDate) {
      return getISODateFromDate(startDate)
    } else {
      return undefined
    }
  }
  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      let change: PlanChange | undefined

      const details: ILongTermCareStrategyChange = {
        plans: [] // TODO
      }
      if (owner_1) {
        const ltc: ILongTermCarePlan = {
          ownerId: owner_1.id,
          strategyType: strategyType_1,
        }
        if (strategyType_1 !== LTCStrategyType.None) {
          ltc.careStart = getCareStartDate(owner_1, careDuration_1!)
          ltc.careDuration = careDuration_1
          ltc.careAmount = moneyToNumberFormat(careAmount_1, 0) ?? 0
          ltc.careFrequency = careFrequency_1
          ltc.careDuration = careDuration_1!
          ltc.careAnnualInf = careAnnualInf_1 !== undefined ? parseFloat(careAnnualInf_1) / 100.0 : undefined
          ltc.assetId =  asset_1 ? asset_1.id : undefined

          if (strategyType_1 === LTCStrategyType.LTCInsurance) {
            ltc.premiumAmount = moneyToNumberFormat(premiumAmount_1, 0) ?? 0
            ltc.premiumAnnualInf = premiumAnnualInf_1 !== undefined ? parseFloat(premiumAnnualInf_1) / 100.0 : 0
            ltc.premiumStart =  premiumStart_1
            ltc.premiumFrequency = premiumFrequency_1
            ltc.premiumWaiver = premiumWaiver_1
            ltc.benefitAmount = moneyToNumberFormat(benefitAmount_1, 0)
            ltc.benefitAnnualInf = benefitAnnualInf_1 !== undefined ? parseFloat(benefitAnnualInf_1) / 100.0 : 0
            ltc.benefitFrequency = benefitFrequecy_1
            ltc.benefitDuration = benefitDuration_1
          }
        }
        details.plans.push(ltc)
      }

      if (owner_2) {
        const ltc: ILongTermCarePlan = {
          ownerId: owner_2.id,
          strategyType: strategyType_2,
        }
        if (strategyType_2 !== LTCStrategyType.None) {
          ltc.careStart = getCareStartDate(owner_2, careDuration_2!)
          ltc.careDuration = careDuration_2
          ltc.careAmount = moneyToNumberFormat(careAmount_2, 0) ?? 0
          ltc.careFrequency = careFrequency_2
          ltc.careDuration = careDuration_2!
          ltc.careAnnualInf = careAnnualInf_2 !== undefined ? parseFloat(careAnnualInf_2) / 100.0 : undefined
          ltc.assetId =  asset_2 ? asset_2.id : undefined

          if (strategyType_2 === LTCStrategyType.LTCInsurance) {
            ltc.premiumAmount = moneyToNumberFormat(premiumAmount_2, 0) ?? 0
            ltc.premiumAnnualInf = premiumAnnualInf_2 !== undefined ? parseFloat(premiumAnnualInf_2) / 100.0 : 0
            ltc.premiumStart =  premiumStart_2
            ltc.premiumFrequency = premiumFrequency_2
            ltc.premiumWaiver = premiumWaiver_2
            ltc.benefitAmount = moneyToNumberFormat(benefitAmount_2, 0)
            ltc.benefitAnnualInf = benefitAnnualInf_2 !== undefined ? parseFloat(benefitAnnualInf_2) / 100.0 : 0
            ltc.benefitFrequency = benefitFrequecy_2
            ltc.benefitDuration = benefitDuration_2
          }
        }
        details.plans.push(ltc)
      }

      if (planChange && !planChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: planChange?.accountId,
          userId: planChange.userId,
          modelId: planChange.modelId,
          planId: planChange.planId,
          changeType: planChange.changeType,
          name: planChange.name,
          description: generateDescription(),
          enabled: true,
          details: JSON.stringify(details)
        }
        change = await modelStore.createPlanChange(input, true)
        Tracking.event({action: "Long Term Strategy Created"})
      } else if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: generateDescription(),
          enabled: true,
          details: JSON.stringify(details)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Social Security Strategy Updated"})
      }

      // if (change && commitChanges) {
      //   await change.commit(model, modelStore)
      // }

      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    let array: string[] = []
    if (owner_1) {
      array.push(`${owner_1.nickname}: ${strategyType_1 === LTCStrategyType.LTCInsurance ? "Long Term Care Ins" : humanizeString(strategyType_1)}`)
    }
    if (owner_2) {
      array.push(`${owner_2.nickname}: ${strategyType_2 === LTCStrategyType.LTCInsurance ? "Long Term Care Ins" : humanizeString(strategyType_2)}`)
    }
    return array.join(", ")
  }

  const renderAge = (age: number) => {
    let text: string | undefined
    const years = Math.trunc(age)
    const months = Math.round((age - years) * 12)
    if (years === 0) {
      text = `${months} mos`
    } else if (months === 0) {
      text = `${years}`
    } else {
      text = `${years} yrs ${months} mos`
    }
    return text
  }

  const getEndDateHelperText = (owner?: Person, start?: string, duration?: Duration) => {
    let text: string | undefined = undefined
    if (owner && start && duration) {
      const startDate = model.getDate(start)
      let endDate
      let age
      if (startDate) {
        if (duration.units === DurationUnits.Months) {
          endDate = addMonths(startDate, duration.amount)
          age = owner.getAgeOnDate(endDate)
        } else if (duration.units === DurationUnits.Years) {
          endDate = addYears(startDate, duration.amount)
          age = owner.getAgeOnDate(endDate)
        } else if (duration.units === DurationUnits.Lifetime) {
          age = owner.getLifeExpectancyYears()
        }
        if (age) {
          text = `Age: ${renderAge(age)}`
        }
      }
    }

    return text
  }

  const getMilestoneDate = (value: string, milestones: Milestone[]) => {
    let isoDate = ""
    let milestone
    if (value.startsWith("age")) {
      milestone = milestones.find((m: Milestone) => m.id === value)
      if (milestone) {
        isoDate = milestone.date
      }
    } else {
      isoDate = value
    }
    return isoDate
  }

  const getCareDurationHelperText = (owner?: Person, careDuration?: Duration) => {
    let text: string = ""

    if (owner && careDuration) {
      if (careDuration.amount === 0) {
        text = "None"
      } else {
        const lifespan = owner.getLifeExpectancyYears()
        let startAge
        if (careDuration.units === DurationUnits.Months) {
          startAge = lifespan - (careDuration.amount / 12)
        } else if (careDuration.units === DurationUnits.Years) {
          startAge = lifespan - careDuration.amount
        }
        if (startAge) {
          text = `Age ${renderAge(startAge)} - Age ${renderAge(lifespan)}`
        }
      }
    }

    return text
  }

  if (!planChange) {
    return null
  }

  const ltcCalcLink = <Link href="https://www.genworth.com/aging-and-you/finances/cost-of-care" target="_blank" sx={styles.calcLink}>
    Click for LTC Calculator
  </Link>
  const endDateHelperText_1 = getCareDurationHelperText(owner_1, careDuration_1)
  const endDateHelperText_2 = getEndDateHelperText(owner_2, careStart_2, careDuration_2)

  return (
    <ModelEditDialog title="Long Term Care Strategy" open={isOpen} size={model.persons.length > 1 ? "md" : "sm"}
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      <DialogContentText>
        Create a strategy for potential long term care expenses.
      </DialogContentText>
      <Grid container spacing={2} pt={1.5}>
        {owner_1 &&
          <Grid item xs={12} sm={model.persons.length > 1 ? 6 : 12} direction="column">
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h5">{owner_1.nickname}'s Long Term Care Strategy</Typography>
                <FormGroup>
                  <RadioGroup aria-label="strategy type 1" name="strategyType_1" value={strategyType_1} row
                              onChange={(event: any) => {
                                setStrategyType_1(event.target.value)
                              }}>
                    <FormControlLabel
                      value="None"
                      control={<Radio color="secondary" />}
                      label="None"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="SelfInsure"
                      control={<Radio color="secondary" />}
                      label="Self Insure"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="LTCInsurance"
                      control={<Radio color="secondary" />}
                      label="LTC Insurance"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormGroup>
              </Grid>
            </Grid>

            {strategyType_1 !== LTCStrategyType.None &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pb:0}}>LTC Cost Estimate</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldValidator
                      type="string"
                      validators={{required: true, isMoney: true}}
                      name="careAmount_1"
                      variant="standard"
                      margin="dense"
                      label="Monthly LTC Cost Est"
                      value={careAmount_1}
                      fullWidth
                      helperText={ltcCalcLink}
                      onChange={(event: any) => setCareAmount_1(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldValidator
                      type="string"
                      validators={{ required: false }}
                      name="careAnnualInf_1"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      label="Inflation %"
                      value={careAnnualInf_1}
                      onChange={(event: any) => setCareAnnualInf_1(event.target.value)}
                      helperText={`Default: ${numberToPercentFormat(model.getDefaultInflationRate("none"), 2)}`}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{display:"flex", flexDirection:"column", justifyContent:"center"}}>
                    <DurationPickerValidator
                      name="careDuration_1"
                      label="LTC Duration (at end of life)"
                      value={careDuration_1}
                      required={true}
                      helperText={endDateHelperText_1}
                      onChange={(value: Duration) => {
                        setCareDuration_1(value)
                      }}
                    />
                  </Grid>
                </Grid>

              </Box>
            }
            {strategyType_1 === LTCStrategyType.LTCInsurance &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pt:0}}>LTC Insurance</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                      <TextFieldValidator
                        type="string"
                        validators={{isMoney: true}}
                        name="premiumAmount_1"
                        variant="standard"
                        margin="dense"
                        label="Premium $"
                        value={premiumAmount_1}
                        fullWidth
                        onChange={(event: any) => setPremiumAmount_1(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextFieldValidator
                        type="string"
                        validators={{ required: false }}
                        name="premiumAnnualInf_1"
                        variant="standard"
                        margin="dense"
                        fullWidth
                        label="Inflation %"
                        value={premiumAnnualInf_1}
                        onChange={(event: any) => setPremiumAnnualInf_1(event.target.value)}
                        helperText={`Default: 0%`}
                      />
                    </Grid>
                    <Grid item xs={5} mt={1}>
                      <TextFieldValidator
                        type="text"
                        validators={{ required: true }}
                        // required
                        name="premiumFrequency_1"
                        label="Frequency"
                        variant="standard"
                        autocompleteOptions={{
                          freeSolo: false,
                          disableClearable: true,
                          options: [Frequency.Monthly, Frequency.Yearly],
                          value: premiumFrequency_1 || null,
                          onChange: (event: any, value: Frequency, reason: any) => {
                            setPremiumFrequency_1(value)
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <MilestoneDateFieldValidator
                      name="premiumStart1"
                      label="Premium Start"
                      milestones={ageMilestones_1}
                      value={premiumStart_1}
                      required={strategyType_1 === LTCStrategyType.LTCInsurance}
                      width="100%"
                      onChange={(value: string) => {
                        setPremiumStart_1(getMilestoneDate(value, ageMilestones_1))
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{display:"flex", flexDirection:"column", justifyContent:"flex-end", pb:1}}>
                    <FormControlLabel control={
                      <Checkbox checked={premiumWaiver_1}
                                onChange={(event: any) => {
                                  setPremiumWaiver_1(event.target.checked)
                                }}
                                sx={{margin:0, padding:0, paddingRight:1}}
                      />}
                      label="Premium waiver at start of care"
                      sx={{ margin:0, padding:0}}
                      componentsProps={{
                        typography: {
                          variant:"body2"
                        }}
                      }
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pt:1}}>LTC Benefit</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                      <TextFieldValidator
                        type="string"
                        validators={{isMoney: true}}
                        name="benefitAmount_1"
                        variant="standard"
                        margin="dense"
                        label="Monthly Max Benefit"
                        value={benefitAmount_1}
                        fullWidth
                        onChange={(event: any) => setBenefitAmount_1(event.target.value)}
                      />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldValidator
                      type="string"
                      validators={{ required: false }}
                      name="benefitAnnualInf_1"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      label="Inflation %"
                      value={benefitAnnualInf_1}
                      onChange={(event: any) => setBenefitAnnualInf_1(event.target.value)}
                      helperText={`Default: 0%`}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <DurationPickerValidator
                      name="benefitDuration_1"
                      label="Max Benefit Duration"
                      value={benefitDuration_1}
                      required={true}
                      onChange={(value: Duration) => {
                        setBenefitDuration_1(value)
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            }
            {strategyType_1 !== LTCStrategyType.None &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <TextFieldValidator
                    type="text"
                    validators={{ required: false }}
                    // required
                    name="asset_1"
                    label="Preferred Payment Account (optional)"
                    helperText="Pay from this account first for premiums and amounts not covered by insurance. Otherwise follow normal withdrawal order."
                    variant="standard"
                    autocompleteOptions={{
                      freeSolo: false,
                      options: model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets).filter((a: Asset) => !a.assetTypeDef.hasIncomeRider),
                      getOptionLabel: (option: Asset) => option ? option.description : "None",
                      isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                      value: asset_1 || null,
                      onChange: (event: any, value: Asset, reason: any) => {
                        setAsset_1(value)
                      },
                    }}
                  />
                </Grid>
              </Box>
            }
          </Grid>
        }

        {owner_2 &&
          <Grid item xs={12} sm={model.persons.length > 1 ? 6 : 12} direction="column">
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h5">{owner_2.nickname}'s Long Term Care Strategy</Typography>
                <FormGroup>
                  <RadioGroup aria-label="strategy type 1" name="strategyType_2" value={strategyType_2} row
                              onChange={(event: any) => {
                                setStrategyType_2(event.target.value)
                              }}>
                    <FormControlLabel
                      value="None"
                      control={<Radio color="secondary" />}
                      label="None"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="SelfInsure"
                      control={<Radio color="secondary" />}
                      label="Self Insure"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="LTCInsurance"
                      control={<Radio color="secondary" />}
                      label="LTC Insurance"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormGroup>
              </Grid>
            </Grid>

            {strategyType_2 !== LTCStrategyType.None &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pb:0}}>LTC Cost Estimate</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldValidator
                      type="string"
                      validators={{required: true, isMoney: true}}
                      name="careAmount_2"
                      variant="standard"
                      margin="dense"
                      label="Monthly LTC Cost Est"
                      value={careAmount_2}
                      fullWidth
                      helperText={ltcCalcLink}
                      onChange={(event: any) => setCareAmount_2(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldValidator
                      type="string"
                      validators={{ required: false }}
                      name="careAnnualInf_2"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      label="Inflation %"
                      value={careAnnualInf_2}
                      onChange={(event: any) => setCareAnnualInf_2(event.target.value)}
                      helperText={`Default: ${numberToPercentFormat(model.getDefaultInflationRate("none"), 2)}`}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{display:"flex", flexDirection:"column", justifyContent:"center"}}>
                    <DurationPickerValidator
                      name="careDuration_2"
                      label="LTC Duration (at end of life)"
                      value={careDuration_2}
                      required={true}
                      helperText={endDateHelperText_2}
                      onChange={(value: Duration) => {
                        setCareDuration_2(value)
                      }}
                    />
                  </Grid>
                </Grid>

              </Box>
            }
            {strategyType_2 === LTCStrategyType.LTCInsurance &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pt:0}}>LTC Insurance</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                      <TextFieldValidator
                        type="string"
                        validators={{isMoney: true}}
                        name="premiumAmount_2"
                        variant="standard"
                        margin="dense"
                        label="Premium $"
                        value={premiumAmount_2}
                        fullWidth
                        onChange={(event: any) => setPremiumAmount_2(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextFieldValidator
                        type="string"
                        validators={{ required: false }}
                        name="premiumAnnualInf_2"
                        variant="standard"
                        margin="dense"
                        fullWidth
                        label="Inflation %"
                        value={premiumAnnualInf_2}
                        onChange={(event: any) => setPremiumAnnualInf_2(event.target.value)}
                        helperText={`Default: 0%`}
                      />
                    </Grid>
                    <Grid item xs={5} mt={1}>
                      <TextFieldValidator
                        type="text"
                        validators={{ required: true }}
                        // required
                        name="premiumFrequency_2"
                        label="Frequency"
                        variant="standard"
                        autocompleteOptions={{
                          freeSolo: false,
                          disableClearable: true,
                          options: [Frequency.Monthly, Frequency.Yearly],
                          value: premiumFrequency_2 || null,
                          onChange: (event: any, value: Frequency, reason: any) => {
                            setPremiumFrequency_2(value)
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <MilestoneDateFieldValidator
                      name="premiumStart1"
                      label="Premium Start"
                      milestones={ageMilestones_2}
                      value={premiumStart_2}
                      required={strategyType_2 === LTCStrategyType.LTCInsurance}
                      width="100%"
                      onChange={(value: string) => {
                        setPremiumStart_2(getMilestoneDate(value, ageMilestones_2))
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{display:"flex", flexDirection:"column", justifyContent:"flex-end", pb:1}}>
                    <FormControlLabel control={
                      <Checkbox checked={premiumWaiver_2}
                                onChange={(event: any) => {
                                  setPremiumWaiver_2(event.target.checked)
                                }}
                                sx={{margin:0, padding:0, paddingRight:1}}
                      />}
                      label="Premium waiver at start of care"
                      sx={{ margin:0, padding:0}}
                      componentsProps={{
                        typography: {
                          variant:"body2"
                        }}
                      }
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{pt:1}}>LTC Benefit</Typography>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                      <TextFieldValidator
                        type="string"
                        validators={{isMoney: true}}
                        name="benefitAmount_2"
                        variant="standard"
                        margin="dense"
                        label="Monthly Max Benefit"
                        value={benefitAmount_2}
                        fullWidth
                        onChange={(event: any) => setBenefitAmount_2(event.target.value)}
                      />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldValidator
                      type="string"
                      validators={{ required: false }}
                      name="benefitAnnualInf_2"
                      variant="standard"
                      margin="dense"
                      fullWidth
                      label="Inflation %"
                      value={benefitAnnualInf_2}
                      onChange={(event: any) => setBenefitAnnualInf_2(event.target.value)}
                      helperText={`Default: 0%`}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <DurationPickerValidator
                      name="benefitDuration_2"
                      label="Max Benefit Duration"
                      value={benefitDuration_2}
                      required={true}
                      onChange={(value: Duration) => {
                        setBenefitDuration_2(value)
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            }
            {strategyType_2 !== LTCStrategyType.None &&
              <Box sx={styles.section}>
                <Grid item xs={12}>
                  <TextFieldValidator
                    type="text"
                    validators={{ required: false }}
                    // required
                    name="asset_2"
                    label="Preferred Payment Account (optional)"
                    helperText="Pay from this account first for premiums and amounts not covered by insurance. Otherwise follow normal withdrawal order."
                    variant="standard"
                    autocompleteOptions={{
                      freeSolo: false,
                      options: model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets).filter((a: Asset) => !a.assetTypeDef.hasIncomeRider),
                      getOptionLabel: (option: Asset) => option ? option.description : "None",
                      isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                      value: asset_2 || null,
                      onChange: (event: any, value: Asset, reason: any) => {
                        setAsset_2(value)
                      },
                    }}
                  />
                </Grid>
              </Box>
            }
          </Grid>
        }
      </Grid>
    </ModelEditDialog>
  )
}
export default LongTermCareStrategyDialog